import { Component, OnInit, Input } from '@angular/core';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';
import { ModalController } from '@ionic/angular';
import { ApiService } from '../services/api.service';
import { ToastService } from '../shared/toast.service';
import { StripeConnectAccountResponse, StripeConnectOnboardingResponse } from '../models/stripe-connect';

@Component({
  selector: 'app-stripe-connect-onboarding',
  templateUrl: './stripe-connect-onboarding.page.html',
  styleUrls: ['./stripe-connect-onboarding.page.scss'],
})
export class StripeConnectOnboardingPage implements OnInit {
  @Input() businessId: number;
  isLoading = false;
  stripeAccountId: string | null = null;

  constructor(
    private modalCtrl: ModalController,
    private apiService: ApiService,
    private toastService: ToastService
  ) {}

  ngOnInit() {
    // Check if business already has a Stripe account
    this.checkExistingStripeAccount();

    // Listen for deep link return
    try {
      App.addListener('appUrlOpen', async (data: any) => {
        const url: string = data?.url || '';
        if (url.startsWith('fixdd://stripe-return')) {
          // Close in-app browser if open
          try { await Browser.close(); } catch (e) {}
          // Refresh status
          await this.checkExistingStripeAccount();
          this.toastService.showToast('Stripe setup complete', 'success');
          // Close this modal and signal parent to refresh
          try { this.modalCtrl.dismiss({ updated: true }); } catch (e) {}
        }
      });
    } catch (e) { /* noop */ }
  }

  async checkExistingStripeAccount() {
    try {
      const response = await this.apiService.getBusinessStripeAccount(this.businessId).toPromise();
      if (response && response.stripeAccountId) {
        this.stripeAccountId = response.stripeAccountId;
      }
    } catch (error) {
      console.error('Error checking Stripe account:', error);
    }
  }

  async startOnboarding() {
    this.isLoading = true;
    try {
      const isNative = !!(window as any).Capacitor?.isNativePlatform;
      const response = await this.apiService.createStripeConnectAccount(this.businessId, isNative ? 'mobile' : 'web').toPromise();
      if (response && response.url) {
        // Open the Stripe Connect onboarding URL
        if ((window as any).Capacitor?.isNativePlatform) {
          await Browser.open({ url: response.url, presentationStyle: 'popover' });
          // After returning, refresh status
          await this.checkExistingStripeAccount();
        } else {
          window.location.href = response.url;
        }
      }
    } catch (error) {
      console.error('Error starting Stripe Connect onboarding:', error);
      this.toastService.showToast('Stripe Account setup is not working right now, please try again later', 'error');
    } finally {
      this.isLoading = false;
    }
  }

  async connectExisting() {
    this.isLoading = true;
    try {
      const isNative = !!(window as any).Capacitor?.isNativePlatform;
      const response = await this.apiService.createStripeConnectOAuth(this.businessId, isNative ? 'mobile' : 'web').toPromise();
      if (response && response.url) {
        if ((window as any).Capacitor?.isNativePlatform) {
          await Browser.open({ url: response.url, presentationStyle: 'popover' });
          // After returning from OAuth, refresh account status
          await this.checkExistingStripeAccount();
        } else {
          window.location.href = response.url;
        }
      }
    } catch (error) {
      console.error('Error starting Stripe OAuth connect:', error);
      this.toastService.showToast('Stripe connection failed, please try again later', 'error');
    } finally {
      this.isLoading = false;
    }
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }
} 